import styled from '@emotion/styled'
import { TextLink } from '../../../../../typography/TextLink/TextLink'
import { colors } from '../../../../../__styling/settings/colors'
import { zIndex } from '../../../../../__styling/settings/z-index'
import { calcRem } from '../../../../../__styling/style-functions/calc-rem'
import {
    fluidTypography,
    miniDivider,
    transition,
} from '../../../../../__styling/style-mixins'
import { themedValue } from '../../../../../__styling/themed-value'
import { Theme } from '../../../../../__styling/themes'

export const StyledBlackFridayBreachContent = styled('div')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    position: 'relative',
    zIndex: zIndex.common.regWall.box,
    fontFamily: theme.fonts.serif,
    color: colors.white,
    paddingTop: calcRem(theme.margins.lg),
    paddingBottom: calcRem(theme.margins.md),
    background: colors.black,
}))

export const StyledRegistrationInfo = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',

    flexBasis: 250,
    flexGrow: 1,
    margin: calcRem(0, theme.margins.md, 0, theme.margins.xl),
}))

const fluidTitle = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(0, 1000, 24, 30),
        perthnow: fluidTypography(0, 1020, 24, 30),
        thewest: fluidTypography(0, 1220, 24, 42),
    })

export const StyledTitle = styled('h2')(({ theme }) => ({
    position: 'relative',
    margin: calcRem(0, 0, theme.margins.md, 0),
    fontWeight: 500,
    lineHeight: 1.267,
    color: colors.thewest.sunshineYellow,
    ...fluidTitle(theme),

    // Pseudo element divider
    ...miniDivider({
        theme: theme,
        dividerColor: colors.darkGrey,
    }),
}))

export const StyledTextLink = styled(TextLink)<{
    setBlackFridayColorScheme: boolean
}>(({ setBlackFridayColorScheme }) => ({
    color: setBlackFridayColorScheme ? colors.white : undefined,
    textDecoration: 'none',

    '&:focus': {
        color: setBlackFridayColorScheme
            ? colors.thewest.sunshineYellow
            : undefined,
        textDecoration: 'underline',
    },
}))

const fluidMessage = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(0, 1000, 24, 30),
        perthnow: fluidTypography(0, 1020, 24, 30),
        thewest: fluidTypography(0, 1220, 16, 24),
    })

export const StyledMessage = styled('div')(({ theme }) => ({
    marginBottom: calcRem(theme.margins.lg),
    ...fluidMessage(theme),

    p: {
        margin: 0,
    },
}))

export const StyledHelpMessage = styled('p')(({ theme }) => ({
    margin: calcRem(theme.margins.sm, 0),
}))

export const StyledPackage = styled('div')(({ theme }) => ({
    flexGrow: 1,
    flexBasis: 400,
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'space-around',
    margin: calcRem(0, theme.margins.md),

    '@media screen and (max-width: 374px)': {
        flexWrap: 'wrap',

        'div:last-of-type': {
            marginLeft: 0,
        },
    },
}))

export const StyledButton = styled('button')(({ theme }) => ({
    display: 'inline-block',
    alignSelf: 'center',
    border: 'none',
    padding: calcRem(theme.margins.sm, theme.margins.xl),
    background: colors.thewest.sunshineYellow,
    color: colors.black,
    borderRadius: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    fontWeight: 700,
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    ...transition({ properties: ['background', 'color'] }),

    '&:hover': {
        backgroundColor: colors.white,
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.blueKyanite,
            sevennews: colors.sevennews.red,
        }),
    },

    '&:focus': {
        backgroundColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
        }),
        borderColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
        }),
        color: 'currentColor',
        outline: 'none',
        textDecoration: 'underline',
    },
}))

export const StyledButtonCaption = styled('p')(({ theme }) => ({
    alignSelf: 'center',
    fontSize: calcRem(11),
}))
